import React, { useState } from 'react';
import { Slider, Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { getRiskColor, getRiskTitle } from '../../utils/constants/findings';

const RangeSlider = ({
  value,
  onChange,
  min = 0,
  max = 10,
  step = 0.1,
  label,
  disabled = false,
  ranges = [
    { start: 0, end: 3, color: '#4caf50' },   // Green for low range
    { start: 3, end: 7, color: '#ff9800' },   // Orange for medium range
    { start: 7, end: 10, color: '#f44336' },  // Red for high range
  ]
}) => {


  // Get the color for current value
  const getTrackColor = (value) => {
    const range = ranges.find(r => value >= r.start && value <= r.end);
    return range ? range.color : ranges[0].color;
  };

  // Create marks for range boundaries
  const marks = ranges.map(range => ([
    { value: range.start, label: range.start.toString() },
    { value: range.end, label: range.end.toString() }
  ])).flat().filter((mark, index, self) =>
    index === self.findIndex(m => m.value === mark.value)
  );


  return (
    <Box
      sx={{ width: '100%' }}
      onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling up to dropdown
    >
      {/* Min-Max labels */}
      <Box
        onMouseDown={(e) => e.stopPropagation()} // Prevent mousedown on min-max labels
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2
        }}>
        <Box className='flex flex-col' sx={{ textAlign: 'left' }}>
          <Typography variant="caption" gutterBottom>
            Minimum
          </Typography>
          <Typography variant="subtitle2 font-bold" className={`${getRiskColor(min)}`}>
            {min}-{getRiskTitle(min)}
          </Typography>
        </Box>
        <Box className='flex flex-col' sx={{ textAlign: 'right' }}>
          <Typography variant="caption" gutterBottom>
            Maximum
          </Typography>
          <Typography variant="subtitle2 font-bold" className={`${getRiskColor(max)}`}>
            {max}-{getRiskTitle(max)}
          </Typography>
        </Box>
      </Box>
      <Slider
        value={value}
        onChange={(e, newValue) => {
          console.log("Slider onChange:", { newValue });
          onChange(e, newValue);
        }}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        valueLabelDisplay="auto"
        sx={{
          '& .MuiSlider-rail': {
            backgroundColor: '#e0e0e0',
            opacity: 1,
            height: 12,
            borderRadius: 0,
          },

          '& .MuiSlider-track': {
            // Transparent track so pseudo-element is visible
            background: 'transparent',
            position: 'relative',
            height: 12,
            borderRadius: 0,
            // Gradient only between the two thumbs
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              right: 0,
              borderRadius: 0,
              background: `linear-gradient(to right,
              ${ranges
                  .map(
                    (r) =>
                      `${r.color} ${(r.start / max) * 100}% ${(r.end / max) * 100}%`
                  )
                  .join(', ')}
            )`,
              zIndex: -1,
            }
          },

          '& .MuiSlider-thumb': {
            backgroundColor: '#ffffff',  // white background
            border: '1px solid #12AFB8',
            width: 18,
            height: 18,
            '&:hover, &.Mui-focusVisible': {
              boxShadow: '0 0 0 8px rgba(18, 175, 184, 0.16)', // subtle hover/focus effect
            }
          }

        }}

      />

      {/* Range indicators */}
      <Box
        onMouseDown={(e) => e.stopPropagation()} // Prevent mousedown on labels from closing dropdown
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 2,
          '& > div': {
            display: 'flex',
            alignItems: 'center',
          }
        }}>
        {ranges.map((range, index) => (
          <div className='flex flex-col gap-y-2'>
            <div key={index}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: range.color,
                  mr: 1
                }}
              />

            </div>
            <Typography className='text-black' variant="body2">
              {getRiskTitle(range.end)}
            </Typography>
            <Typography variant="body2">
              ({range.start}-{range.end})
            </Typography>
          </div>
        ))}
      </Box>

    </Box>
  );
};

RangeSlider.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  ranges: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
    })
  ),
};

export default RangeSlider;